import React, { useState, useEffect, useRef, useId } from 'react';
import Modal from 'react-modal';
import './../../styles/FavouriteTooltip.css';
import './../../styles/ImageModal.css';
import recordService from './../../services/RecordService';
import { COLORS } from './../../constants/colours';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from './../../context/AuthContext';
import HR from './../../utils/HR';
import { UserType } from './../../constants/userType';
import { 
  faTimes, 
  faHistory,
  faCircleArrowLeft, 
  faCircleArrowRight, 
  faEyeSlash, 
  faSearchPlus, 
  faSearchMinus,
  faRotateRight,
  faRotateLeft,
  faExternalLinkAlt,
  faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import formatDateTime from './../../utils/formatDateTime';
import hexToRgba  from './../../utils/hexToRgba';

const ImageModal = ({ onClose, token, images, currentIndex, setCurrentIndex, updateImageInGrid, username }) => {
  const currentImage = images[currentIndex];
  const appObservationTooltipId = useId();
  const observedAppPackageName = currentImage?.imageAppObservation?.currentPackageName?.trim();
  const [comments, setComments] = useState('');
  const [positive, setPositive] = useState('');
  const [status, setStatus] = useState('');
  const [quarantine, setQuarantine] = useState('');
  const [category, setCategory] = useState('');
  const [colour, setColour] = useState('');
  const [reviewedby, setReviewedby] = useState('');
  const [groupName, setGroupName] = useState('');
  const [isBlurred, setIsBlurred] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const positiveNum = Number(currentImage.positive);
  const statusNum   = Number(currentImage.status);
  const quarantineNum = Number(currentImage.quarantine);
  const { userType } = useAuth();
  const [localCache, setLocalCache] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const viewedImageIdsRef = useRef(new Set());
  const LENS_WIDTH     = 300;   // px lens width
  const LENS_HEIGHT    = 220;   // px lens height
  const MAGNIFICATION = 2;      // 2× zoom
  const imgContainerRef = useRef(null);
  const [imgSize, setImgSize]           = useState({ width: 0, height: 0 });
  const [isLensVisible, setLensVisible] = useState(false);
  const [lensPos, setLensPos]           = useState({ x: 0, y: 0 });
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [auditData, setAuditData] = useState([]);
  const isPoliceUser = userType === UserType.POLICE;
  const [decision, setDecision] = useState(null); // 'yes' | 'no' | null
  const [isFlagSaving, setIsFlagSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  useEffect(() => {
 
  if (!currentImage?.image_id) return;

  const imageId = currentImage.image_id;

  if (!viewedImageIdsRef.current.has(imageId)) {
    viewedImageIdsRef.current.add(imageId);

    updateImageInGrid({ image_id: imageId, isViewed: true });

    recordService.setImageAsReviewed(imageId).catch((err) => {
      console.error('❌ Failed to mark image as viewed:', err.message);
    });
  }

    const cached = localCache[currentImage.image_id] || {};
    setPositive(
      cached.positive ?? (positiveNum === 1 ? '1' : positiveNum === 2 ? '2' : '0')
    );
    setStatus(
      cached.status ?? (statusNum === 0 ? '0' : statusNum === 1 ? '1' : '2')
    );
    setQuarantine(
      cached.quarantine ?? (quarantineNum === 1 ? 'Yes' : quarantineNum === 0 ? '1' : '0')
    );
    setCategory(
      cached.category ?? currentImage.report_category ?? ''
    );
    setComments(
      cached.comments ?? currentImage.image_description ?? ''
    );
    setColour(
      (cached.colour ?? (currentImage.colour || '#000'))
    );
    setReviewedby(username || 'None');
    setGroupName(currentImage.groupName || '---');
    setZoom(1);
    setIsBlurred(false);
    setRotation(0);
    setIsExpanded(false);
  

  // Preload next image if available so it loads faster when navigating
  const nextImage = images[currentIndex + 1];
  if (nextImage && nextImage.url) {
    const img = new Image();
    img.src = nextImage.url;
  }
}, [currentIndex, currentImage, currentImage?.image_id]);

  // when the <img> loads, record its displayed size
  const onImageLoad = e => {
    setImgSize({
      width: e.target.width,
      height: e.target.height
    });
  };

  // track mouse and compute lens position
  const handleMouseMove = e => {
    const rect = imgContainerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    // clamp so lens never goes off edges
 x = Math.max(0, Math.min(x, rect.width));
   y = Math.max(0, Math.min(y, rect.height));
    setLensPos({ x, y });
  };

  const handlePositiveChange = (value) => {
    setPositive(value);
    let newColour = colour;
  
    if (value === '1') {
      setStatus('0');
      newColour = COLORS.POSITIVE_IMPORTANT;
    } else if (value === '2') {
      setStatus('1');
      newColour = COLORS.FALSE_POSITIVE;
    }
    else if (value === '0') {
      setStatus('2');
      setQuarantine('0');
      setCategory('');
      setComments('');
      newColour = COLORS.NOT_REVIEWED;
    }
  
    setColour(newColour);
    setReviewedby(username || 'None');
  
    // ✅ Update cache when positive changes
    setLocalCache(prev => ({
      ...prev,
      [currentImage.image_id]: {
        ...prev[currentImage.image_id],
        positive: value,
        status: value === '1' ? '0' : value === '2' ? '1' : '2',
        quarantine: value === '0' ? '0' : quarantine,
        category: value === '0' ? '' : category,
        comments: value === '0' ? '' : comments,
        colour: newColour
      }
    }));
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    let newPositive = positive;
    let newColour = colour;
  
    if (value === '0') {
      if (positive === '2') {
        newPositive = '2';
        newColour = COLORS.FALSE_POSITIVE_IMPORTANT;
    } else {
        newPositive = '1';
        newColour = COLORS.POSITIVE_IMPORTANT;
      }
    } else if (value === '1') {

      if (positive === '1') {
        newPositive = '1';
        newColour = COLORS.POSITIVE_NOT_IMPORTANT;
    } else {
        newPositive = '2'
        newColour = COLORS.FALSE_POSITIVE;
      }

     // newPositive = '2';
     // newColour = COLORS.FALSE_POSITIVE;
    }
    else if (value === '2') {
      newPositive = '0';
      setQuarantine('0');
      setCategory('');
      setComments('');
      newColour = COLORS.NOT_REVIEWED;
    }
  
    setPositive(newPositive);
    setColour(newColour);
  
    setLocalCache(prev => ({
      ...prev,
      [currentImage.image_id]: {
        ...prev[currentImage.image_id],
        positive: newPositive,
        status: value,
        quarantine: value === '2' ? '0' : quarantine,
        category: value === '2' ? '' : category,
        comments: value === '2' ? '' : comments,
        colour: newColour
      }
    }));
  };

  const handleQuarantineChange = (value) => {
    setQuarantine(value);
    if (value === 'Yes') {
      setPositive('1');
      setStatus('0');
      setColour(COLORS.QUARANTINE);
    }
    else if (value === 'No') {
      if(positive === '1' && status === '0') {
        setColour(COLORS.POSITIVE_IMPORTANT);
      }
      else if (positive === '2' && status === '1') {
        setColour(COLORS.FALSE_POSITIVE);
      }
      else if (positive === '0' && status === '2') {
        setColour(COLORS.NOT_REVIEWED);
      }
    }
    
  };

  const handleSave = async () => {
    const trimmedCategory = category || '';
    const normalizedComments = comments || '';
    const updatedRecord = {
      image_id: currentImage.image_id,
      imageId: currentImage.image_id,
      recordId: currentImage.image_id,
      image_description: normalizedComments,
      imageDescription: normalizedComments,
      description: normalizedComments,
      severity: currentImage.severity,
      positive: positive,
      report_category: trimmedCategory,
      reportCategory: trimmedCategory,
      category: trimmedCategory,
      status: status,
      action: currentImage.action,
      colour: colour,
      quarantine: quarantine === 'Yes' ? 1 : 0,
      reviewedby: reviewedby,
    };
    
    try {
      await recordService.updateRecord(updatedRecord);  
      updateImageInGrid(updatedRecord);  
      setLocalCache(prev => {
        const next = { ...prev };
        delete next[currentImage.image_id];
        return next;
      });
  
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds
  
    } catch (error) {
      console.error("❌ Failed to update record:", error.message);
    }
  };
  
  const toggleBlur = () => {
    setIsBlurred(prev => !prev);
  };

  const zoomIn = () => {
    setZoom(prev => prev + 0.2);
  };

  const zoomReset = () => {
    setZoom(1);
    setRotation(0);
  };

  const zoomOut = () => {
    setZoom(prev => (prev > 0.5 ? prev - 0.1 : prev));
  };

  const rotateLeft = () => {
    setRotation(prev => prev - 90);
  };

  const rotateRight = () => {
    setRotation(prev => prev + 90);
  };

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  const openInNewTab = () => {
    window.open(currentImage.url, '_blank');
  };

const openAuditModal = async () => {
  setIsAuditLoading(true);
  setIsAuditModalOpen(true);           // open the modal immediately (shows loader)
  try {
    const result = await recordService.getRecordsAuditById(currentImage.image_id);
    const entries = Array.isArray(result?.data) ? result.data : [];
    setAuditData(entries);
  } catch (err) {
    setAuditData([]);                  // or keep previous – your call
  } finally {
    setIsAuditLoading(false);
  }
};

  const closeAuditModal = () => {
    setIsAuditModalOpen(false);
  };

const normalizeAction = (val) => {
  if (val === null || val === undefined) return null;

  // booleans
  if (typeof val === 'boolean') return val;

  // numbers
  if (typeof val === 'number') return val === 1;

  // strings
  if (typeof val === 'string') {
    const v = val.trim().toLowerCase();
    if (v === '1' || v === 'true' || v === 'yes' || v === 'y' || v === 'action required') return true;
    if (v === '0' || v === 'false' || v === 'no'  || v === 'n' || v === 'no action')       return false;

    // fallback: try numeric
    const n = Number(v);
    if (!Number.isNaN(n)) return n === 1;
  }
  return null;
};


const setActionFlag = async (isAction) => {
  if (!isPoliceUser) return;

  const prevDecision = decision;
  const nextDecision = isAction ? 'yes' : 'no';
  const nextActionNum = isAction ? 1 : 2;
  const derivedColour = isAction ? COLORS.ACTION_REQUIRED : COLORS.ACTION_NOT_REQUIRED;

  setDecision(nextDecision);
  setIsFlagSaving(true);

  setColour(derivedColour);

  try {
    await recordService.setAction(currentImage.image_id, !!isAction);

    updateImageInGrid({
      image_id: currentImage.image_id,
      action: nextActionNum,
      colour: derivedColour
    });

    setLocalCache(prev => ({
      ...prev,
      [currentImage.image_id]: {
        ...prev[currentImage.image_id],
        action: nextActionNum,
        colour: derivedColour
      }
    }));

    setToastMsg(isAction ? 'Flagged as Action Required' : 'Marked as No Action');
    setTimeout(() => setToastMsg(null), 2200);
  } catch (err) {
    setDecision(prevDecision);
    const revertColour =
      prevDecision === 'yes' ? COLORS.ACTION_REQUIRED :
      prevDecision === 'no'  ? COLORS.ACTION_NOT_REQUIRED :
      (currentImage?.colour || '#000');
    setColour(revertColour);

    setToastMsg('Failed to update action flag.');
    setTimeout(() => setToastMsg(null), 2500);
  } finally {
    setIsFlagSaving(false);
  }
};

const chooseNoAction = () => setActionFlag(false);
const chooseActionRequired = () => setActionFlag(true);

useEffect(() => {
  const cachedAction = localCache[currentImage?.image_id]?.action;
  const effective = cachedAction ?? currentImage?.action;
  const s = typeof effective === 'string' ? effective.trim() : effective;

  if (s === 1 || s === '1')      setDecision('yes');
  else if (s === 2 || s === '2') setDecision('no');
  else                           setDecision(null); // anything else stays unset
}, [currentImage?.image_id, currentImage?.action, localCache]);


  return (
     <>
    <Modal
      isOpen={!!currentImage}
      onRequestClose={onClose}
      contentLabel="Image Details"
      ariaHideApp={false}
      className="image-modal"
      overlayClassName="image-modal-overlay"
     // key={currentImage.image_id}
    >
      <div className="image-modal-content">
      {saveSuccess && (
  <div className="toast-message success">
    Saved successfully!
  </div>
)}
        <div className="modal-header apple-style-header">
          <div className="modal-title">Screen Violation</div>
          <div className="modal-action-buttons">
            <button className="modal-btn" onClick={toggleBlur} title="Toggle Blur">
              <FontAwesomeIcon icon={faEyeSlash} />
            </button>
            <button className="modal-btn" onClick={zoomIn} title="Zoom In">
              <FontAwesomeIcon icon={faSearchPlus} />
            </button>
            <button className="modal-btn" onClick={zoomReset} title="Zoom Reset">
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
            <button className="modal-btn" onClick={zoomOut} title="Zoom Out">
              <FontAwesomeIcon icon={faSearchMinus} />
            </button>
            <button className="modal-close-btn" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div className="modal-navigation">
          <button
            className="arrow-button"
            onClick={() => setCurrentIndex(currentIndex - 1)}
            disabled={currentIndex === 0}
          >
            <FontAwesomeIcon icon={faCircleArrowLeft} />
          </button>
          <div className="image-count">
            {currentIndex + 1} / {images.length}
          </div>
          <button
            className="arrow-button"
            onClick={() => setCurrentIndex(currentIndex + 1)}
            disabled={currentIndex === images.length - 1}
          >
            <FontAwesomeIcon icon={faCircleArrowRight} />
          </button>
        </div>
        
        <div className="modal-body compact-modal">
          {currentImage && !isExpanded && (
            <>
              <div
                className="image-preview-wrapper"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  overflow: 'visible', // allow lens to spill outside
                }}
              >

  {/* the actual container you measure for the lens */}
  <div
    className="image-preview"
    ref={imgContainerRef}
    onMouseEnter={() => setLensVisible(true)}
    onMouseMove={handleMouseMove}
    onMouseLeave={() => setLensVisible(false)}
    style={{
      position: 'relative',
      display: 'inline-block',
      overflow: 'visible'        // ditto here
    }}
  >
    <img
      src={currentImage.url}
      alt={currentImage.description || ''}
      className="img-fluid modal-image-large"
      onLoad={onImageLoad}
      style={{
        border: `6px solid ${colour === 'WHITE' ? 'transparent' : colour}`,
        transform: `scale(${zoom}) rotate(${rotation}deg)`,
        filter: isBlurred ? 'blur(20px)' : 'none',
        display: 'block',
        maxWidth: '100%',
      }}
      onClick={toggleExpanded}
    />

    {isLensVisible && imgSize.width > 0 && (
      <div
        className="image-lens"
        style={{
          position: 'absolute',
          left:  `${lensPos.x - LENS_WIDTH/2}px`,
          top:   `${lensPos.y - LENS_HEIGHT/2}px`,
          width:  `${LENS_WIDTH}px`,
          height: `${LENS_HEIGHT}px`,
          border: '2px solid rgba(0,0,0,0.5)',
          /* no borderRadius → rectangular */
          pointerEvents: 'none',
          backgroundImage:   `url(${currentImage.url})`,
          backgroundRepeat:  'no-repeat',
          backgroundSize:    `${imgSize.width  * MAGNIFICATION}px ${imgSize.height * MAGNIFICATION}px`,
          backgroundPosition: `
            ${-lensPos.x * MAGNIFICATION + LENS_WIDTH  / 2}px
            ${-lensPos.y * MAGNIFICATION + LENS_HEIGHT / 2}px
          `
        }}
      />
    )}
  </div></div>


              <HR />
              <div className="center-content">
                <div className="status-group">
                  <div className="form-group-status-inline">                    
                    <label>Visor Ref:</label> <span className="form-group-status-inline-top-status">{currentImage?.visor_ref || '---'}</span>
                    <div className="vl"></div>
                    <label>Report Date:</label> 
                    <span className="form-group-status-inline-top-status">{formatDateTime(new Date(currentImage?.report_date))}</span>                    
                    { userType === UserType.SUPER && (
                        <>
                          <div className="vl"></div>
                          <label>Reviewed By:</label> <span className="form-group-status-inline-top-status">{currentImage?.reviewedby || 'None'}</span>
                        </>)}                    
                        <div className="vl"></div>
                    <label>Group:</label> <span className="form-group-status-inline-top-status">{groupName || '---'}</span>
                        <div className="vl"></div>
                    <label>Model:</label>
<span className="form-group-status-inline-top-status">
  {currentImage?.model || '---'}
</span>
                    { (userType === UserType.SUPER || userType === UserType.TEAMLEADER) && (
                      <>
                        <div className="vl"></div>
                        <button
  className="audit-btn"
  onClick={openAuditModal}
  title={isAuditLoading ? 'Loading…' : 'View Audit Trail'}
  style={{ marginLeft: '0.5rem', border: 'none', background: 'transparent', cursor: isAuditLoading ? 'wait' : 'pointer' }}
  disabled={isAuditLoading}
  aria-busy={isAuditLoading}
>
  {isAuditLoading
    ? <FontAwesomeIcon icon={faSpinner} spin />
    : <FontAwesomeIcon icon={faHistory} />
  }
</button>
                      </>
                    )}
                  </div>
                  {!isPoliceUser && (
                  <dl
                    className="image-app-observation"
                    tabIndex={0}
                    aria-describedby={appObservationTooltipId}
                  >
                    <dt>Observed foreground app:</dt>
                    <dd>
                      <span>{observedAppPackageName || 'Unavailable'}</span>
                      {observedAppPackageName && (
                        <a
                          className="image-app-search-link"
                          href={`https://www.google.com/search?q=${encodeURIComponent(observedAppPackageName)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Search Google for ${observedAppPackageName} (opens in a new tab)`}
                        >
                          Search online <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      )}
                      <span className="favourite-tooltip" id={appObservationTooltipId} role="tooltip">
                        <span className="favourite-tooltip-body">
                          Based on device observations near capture time. May not identify the app shown in the image.
                        </span>
                      </span>
                    </dd>
                  </dl>
                  )}
                </div>
              </div>
              <HR />
              <div className="center-content">
                <div className="status-group">
                  <div className="form-group-inline">
                    <label>Positive:</label>
                    <select 
                      className="form-control-inline" 
                      value={positive} 
                      onChange={(e) => handlePositiveChange(e.target.value)}
                      disabled={isPoliceUser}
                    >
                      <option value="0">Not Reviewed</option>
                      <option value="1">Yes</option>
                      <option value="2">No</option>
                    </select>
                  
                    <label>Status:</label>
                    <select 
                      className="form-control-inline" 
                      value={status} 
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={isPoliceUser}
                    >
                      <option value="0">Important</option>
                      <option value="1">Not Important</option>
                      <option value="2">Not Reviewed</option>
                    </select>
                  </div>

                  <div className="form-group-inline">
                    <label>Quarantine:</label>
                    <select 
                      className="form-control-inline" 
                      value={quarantine} 
                      onChange={(e) => handleQuarantineChange(e.target.value)}
                      disabled={isPoliceUser}
                    >
                      <option value="0">Select</option>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  
                    <label>Category:</label>
                    <select 
                      className="form-control-inline" 
                      value={category} 
                      onChange={(e) => {
  const value = e.target.value;
  setCategory(value);
  setLocalCache(prev => ({
  ...prev,
  [currentImage.image_id]: {
    ...prev[currentImage.image_id],
    category: value,
    comments,
    positive,
    status
  }
}));
}}
disabled={isPoliceUser}
                    >
                      <option value="">Category</option>
                      <option value="A">A - Illegal activity (not image related)</option>
                      <option value="B">B - Illegal images</option>
                      <option value="C">C - Suspect images (Teen)</option>
                      <option value="D">D - Extreme Pornography</option>
                      <option value="E">E - Pornography</option>
                      <option value="F">F - Suspect nudity</option>
                      <option value="G">G - Nudity</option>
                      <option value="H">H - Grooming</option>
                      <option value="I">I - Stranger Danger</option>
                      <option value="J">J - Sexting</option>
                      <option value="K">K - SHPO Breach</option>
                      <option value="L">L - Self-Harm</option>
                      <option value="M">M - Depression/Negativity</option>
                      <option value="N">N - Illegal Intent</option>
                      <option value="O">O - Extremism</option>
                      <option value="P">P - Concerning</option>
                      <option value="Q">Q - Offender Manager Request</option>
                      <option value="R">R - Computer misuse</option>
                      <option value="S">S - Weaponry & Violence</option>
                      <option value="T">T - Other</option>
                      <option value="U">U - Not Classified</option>
                      <option value="V">V - Not Classified</option>
                      <option value="W">W - Not Classified</option>
                      <option value="X">X - Not Classified</option>
                      <option value="Y">Y - Not Classified</option>
                      <option value="Z">Z - Not Classified</option>
                    </select>
                  </div>
                </div>
                {isPoliceUser && (
  <div className="decision-card">
    <div className="segmented" role="radiogroup" aria-label="Action decision">
      <button
        type="button"
        role="radio"
        aria-checked={decision === 'no'}
        className={`seg-option success ${decision === 'no' ? 'selected' : ''}`}
        onClick={chooseNoAction}
      >
        <span className="seg-icon">✔</span>
        <span>No Action</span>
      </button>

      <button
        type="button"
        role="radio"
        aria-checked={decision === 'yes'}
        className={`seg-option danger ${decision === 'yes' ? 'selected' : ''}`}
        onClick={chooseActionRequired}
      >
        <span className="seg-icon">!</span>
        <span>Action Required</span>
      </button>
    </div>
  </div>
)}
              </div>            
              <div className="form-group-inline">
                <label className="lbl-comments">Comments:</label>
                <textarea 
                  className={isPoliceUser ? "textarea-police-form-control" : "textarea-form-control"} 
                  placeholder="Enter comments..." 
                  value={comments} 
                  onChange={(e) => {
                    const value = e.target.value;
                    setComments(value);
                    setLocalCache(prev => ({
                      ...prev,
                      [currentImage.image_id]: {
                        ...prev[currentImage.image_id],
                        comments: value,
                        category
                      }
                    }));
                  }} 
                  readOnly={isPoliceUser}
                />
              </div>
            </>
          )}

          {currentImage && isExpanded && (
            <div className="expanded-panel">
              <div className="expanded-header">
                <button className="modal-btn" onClick={rotateLeft} title="Rotate Left">
                  <FontAwesomeIcon icon={faRotateLeft} />
                </button>
                <button className="modal-btn" onClick={rotateRight} title="Rotate Right">
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>
                <button className="modal-btn" onClick={openInNewTab} title="Open in New Tab">
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </button>
                <button className="modal-btn" onClick={toggleExpanded} title="Close Expanded View">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="expanded-body">
                <img
                  src={currentImage.url}
                  alt={currentImage.description || ''}
                  className="img-fluid expanded-image"
                  style={{
                    border: `6px solid ${colour}`,
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    filter: isBlurred ? 'blur(20px)' : 'none'
                  }}
                />
              </div>
            </div>
          )}
        </div>
        {!isPoliceUser && !isExpanded && (
          <div className="image-modal-footer">
            <button className="save-btn" onClick={handleSave}>Save</button>
          </div>
        )}
      </div>
    </Modal>

    <Modal
      isOpen={isAuditModalOpen}
      onRequestClose={closeAuditModal}
      contentLabel="Audit Trail"
      ariaHideApp={false}
      className="audit-modal"
      overlayClassName="audit-modal-overlay"
    >
      <div className="audit-header">
        <h2>Audit Trail for Image #{currentImage.image_id}</h2>
        <button className="modal-close-btn" onClick={closeAuditModal}>
          <FontAwesomeIcon icon={faTimes}/>
        </button>
      </div>
      <div className="audit-body">
  {isAuditLoading ? (
    <div className="audit-loading">
      <FontAwesomeIcon icon={faSpinner} spin /> Fetching audit trail…
    </div>
  ) : auditData.length === 0 ? (
    <p>No audit records found.</p>
  ) : (
        <table className="audit-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Reviewer</th>
              <th>Status</th>
              <th>Quarantine</th>
              <th>Positive</th>
              <th>Visor Ref</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(auditData) ? auditData : []).map((entry,i) => (

              <tr key={i} style={{ backgroundColor: hexToRgba(entry.colour, 0.5) }}>
                <td>{entry.timestamp}</td>
                <td>{entry.reviewer}</td>
                <td>{entry.status === 0 ? 'Important' : entry.status === 1 ? 'Not important' : 'Not Reviewed'}</td>
                <td>{entry.quarantine === 1 ? 'Yes' : 'No'}</td>
                <td>{entry.positive === 1 ? 'Yes' : 'No'}</td>
                <td>{entry.visorRef ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
  )}
      </div>
    </Modal>
</>
  );
};

export default ImageModal;
